<template>
  <section class="invoice-preview-wrapper">
    <!--begin::Loader-->
    <invoice-loader v-if="loader" />
    <!--end::Loader-->
    <b-row
      v-else
      class="invoice-preview"
    >

      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <b-card-body class="invoice-padding pb-0">

            <div class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0">

              <!-- Header: Left Content -->
              <div>
                <div class="logo-wrapper">
                  <logo />
                  <h3 class="text-primary invoice-logo">
                    {{ appConfig.brand }}
                  </h3>
                </div>
                <p class="card-text mb-25">
                  {{ appConfig.address }}
                </p>
                <p class="card-text mb-25">
                  {{ appConfig.address_2 }}
                </p>
                <p class="card-text mb-25">
                  {{ appConfig.phone }}
                </p>
                <p class="card-text mb-0">
                  {{ appConfig.mobile }}
                </p>
              </div>

              <!-- Header: Right Content -->
              <div class="mt-md-0 mt-2">
                <h4 class="invoice-title">
                  {{ $t('Invoice') }}
                  <span class="invoice-number">#{{ invoice.id }}</span>
                </h4>

                <div class="invoice-date-wrapper">
                  <p class="invoice-date-title">
                    {{ $t('Status') }}:
                  </p>
                  <display-status
                    :value="invoice.status"
                    :text="invoice.display_status"
                  />
                </div>
                <div class="invoice-date-wrapper">
                  <p class="invoice-date-title">
                    {{ $t('Created at') }}:
                  </p>
                  <p
                    class="invoice-date"
                    style="direction: ltr"
                  >
                    {{ invoice.created_at | localeDateTime }}
                  </p>
                </div>
                <div
                  v-if="invoice.payment.payment_at"
                  class="invoice-date-wrapper"
                >
                  <p class="invoice-date-title">
                    {{ $t('Payment at') }}:
                  </p>
                  <p
                    class="invoice-date"
                    style="direction: ltr"
                  >
                    {{ invoice.payment.payment_at | localeDateTime }}
                  </p>
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

          <!-- Invoice Client & Payment Details -->
          <b-card-body
            v-if="invoice.user"
            class="invoice-padding pt-0"
          >
            <b-row class="invoice-spacing">

              <!-- Col: Invoice To -->
              <b-col
                cols="12"
                xl="6"
                class="p-0"
              >
                <h6 class="mb-2">
                  {{ $t('User info') }}:
                </h6>
                <h6 class="mb-25">
                  {{ invoice.user.name }}
                </h6>
                <p
                  class="card-text mb-25"
                  style="direction: ltr"
                >
                  {{ invoice.user.mobile }}
                </p>
                <p class="card-text mb-25">
                  {{ invoice.user.email }}
                </p>
              </b-col>

              <!-- Col: Payment Details -->
              <b-col
                v-if="false"
                xl="6"
                cols="12"
                class="p-0 mt-xl-0 mt-2 d-flex justify-content-xl-end"
              >
                <div>
                  <h6 class="mb-2">
                    Payment Details:
                  </h6>
                  <table>
                    <tbody>
                      <tr>
                        <td class="pr-1">
                          Total Due:
                        </td>
                        <td><span class="font-weight-bold">5</span></td>
                      </tr>
                      <tr>
                        <td class="pr-1">
                          Bank name:
                        </td>
                        <td>5</td>
                      </tr>
                      <tr>
                        <td class="pr-1">
                          Country:
                        </td>
                        <td>5</td>
                      </tr>
                      <tr>
                        <td class="pr-1">
                          IBAN:
                        </td>
                        <td>5</td>
                      </tr>
                      <tr>
                        <td class="pr-1">
                          SWIFT code:
                        </td>
                        <td>5</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-table-lite
            responsive
            :items="invoice.items"
            :fields="itemFields"
          >
            <template #cell(quantity)="data">
              {{ data.value }}
            </template>
            <template #cell(amount)="data">
              {{ data.value | numberFormat }} {{ $t('Currency') }}
            </template>
            <template #cell(discount)="data">
              {{ data.value | numberFormat }} {{ $t('Currency') }}
            </template>
            <template #cell(total_amount)="data">
              {{ data.value | numberFormat }} {{ $t('Currency') }}
            </template>
          </b-table-lite>

          <!-- Invoice Description: Total -->
          <b-card-body class="invoice-padding pb-0">
            <b-row>

              <!-- Col: Sales Person -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-0 mt-3"
                order="2"
                order-md="1"
              >
                <!--                <b-card-text class="mb-0">-->
                <!--                  <span class="font-weight-bold"></span>-->
                <!--                  <span class="ml-75"></span>-->
                <!--                </b-card-text>-->
              </b-col>

              <!-- Col: Total -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-6 d-flex justify-content-end"
                order="1"
                order-md="2"
              >
                <div
                  class="invoice-total-wrapper"
                  style="max-width: 25rem"
                >
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      {{ $t('Amount') }}:
                    </p>
                    <p class="invoice-total-amount">
                      {{ invoice.payment.amount | numberFormat }} {{ $t('Currency') }}
                    </p>
                  </div>
                  <template v-for="(discount,key) in invoice.payment.discounts">
                    <div
                      v-if="discount.amount > 0"
                      :key="key"
                      class="invoice-total-item text-info"
                    >
                      <p class="invoice-total-title">
                        <span>{{ discount.title }} <span v-if="discount.info.code > 0">({{ discount.info.code }})</span> <span v-if="discount.percent > 0">({{ discount.percent | numberFormat }}%)</span></span>:
                      </p>
                      <p class="invoice-total-amount">
                        {{ discount.amount | numberFormat }} {{ $t('Currency') }}
                      </p>
                    </div>
                  </template>
                  <div
                    v-if="invoice.payment.discount > 0"
                    class="invoice-total-item text-danger"
                  >
                    <p class="invoice-total-title">
                      {{ $t('Total discount') }}:
                    </p>
                    <p class="invoice-total-amount">
                      {{ invoice.payment.discount | numberFormat }} {{ $t('Currency') }}
                    </p>
                  </div>
                  <div class="invoice-total-item text-warning">
                    <p class="invoice-total-title">
                      {{ $t('Tax amount') }} ({{ invoice.payment.tax_percent | numberFormat }}%):
                    </p>
                    <p class="invoice-total-amount">
                      {{ invoice.payment.tax_amount | numberFormat }} {{ $t('Currency') }}
                    </p>
                  </div>
                  <hr class="my-50">
                  <div class="invoice-total-item text-success">
                    <p class="invoice-total-title">
                      {{ invoice.payment.is_paid ? $t('Paid amount') : $t('Payable amount') }}:
                    </p>
                    <p class="invoice-total-amount">
                      {{ invoice.payment.total_amount | numberFormat }} {{ $t('Currency') }}
                    </p>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: DOwnload -->
          <b-button
            v-if="invoice.payment.is_paid"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="success"
            class="mb-75"
            block
          >
            {{ $t('Download') }}
          </b-button>

          <!-- Button: Print -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice"
          >
            {{ $t('Print') }}
          </b-button>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BRow, BCol, BCard, BCardBody, BTableLite, BCardText, BButton, VBToggle,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import Logo from '@core/layouts/components/Logo.vue'
import store from '@/store'
import InvoiceLoader from '../../components/loaders/InvoiceLoader'
import DisplayStatus from '../../components/core/DisplayStatus'

export default {
  name: 'Show',
  components: {
    DisplayStatus,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,

    Logo,
    InvoiceLoader,
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  props: {
    id: {
      type: [String, Number],
      default: () => 0,
    },
  },
  data() {
    return {
      loader: true,
      invoice: {},
      itemFields: [
        {
          key: 'item.name',
          label: this.$t('Item name'),
        },
        {
          key: 'amount',
          label: this.$t('Amount'),
        },
        {
          key: 'quantity',
          label: this.$t('Quantity'),
        },
        {
          key: 'discount',
          label: this.$t('Discount amount'),
        },
        {
          key: 'total_amount',
          label: this.$t('Total amount'),
        },
      ],
    }
  },
  computed: {
    appConfig() {
      return store.state.app.appConfig
    },
  },
  created() {
    this.getInvoice()
  },
  methods: {
    getInvoice() {
      this.loader = true
      this.$http.get(`invoices/${this.id}`).then(({ data }) => {
        this.invoice = data
        this.loader = false
      })
    },
    printInvoice() {
      window.print()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
</style>

<style lang="scss">
@media print {

    // Global Styles
    body {
        background-color: transparent !important;
    }
    nav.header-navbar {
        display: none;
    }
    .main-menu {
        display: none;
    }
    .header-navbar-shadow {
        display: none !important;
    }
    .content.app-content {
        margin-left: 0;
        padding-top: 2rem !important;
    }
    footer.footer {
        display: none;
    }
    .card {
        background-color: transparent;
        box-shadow: none;
    }
    .customizer-toggle {
        display: none !important;
    }

    // Invoice Specific Styles
    .invoice-preview-wrapper {
        .row.invoice-preview {
            .col-md-8 {
                max-width: 100%;
                flex-grow: 1;
            }

            .invoice-preview-card {
                .card-body:nth-of-type(2) {
                    .row {
                        > .col-12 {
                            max-width: 50% !important;
                        }

                        .col-12:nth-child(2) {
                            display: flex;
                            align-items: flex-start;
                            justify-content: flex-end;
                            margin-top: 0 !important;
                        }
                    }
                }
            }
        }

        // Action Right Col
        .invoice-actions {
            display: none;
        }
    }
}
</style>
